import React from 'react'
import { Skeleton, Typography, Button, Form, Input, InputNumber, Switch, Row, Col, message } from 'antd'
import { FormComponent, BaseFormComponentProps } from '../../BaseComponent'
import TableTransfer from '../../TableTransfer'
import ISpecie from '../../../interfaces/ISpecie'
import IGroup from '../../../interfaces/IGroup'
import { SearchRequest, GroupSaveRequest }from '../../../services/api/interfaces/ApiRequest'

type GroupProps = BaseFormComponentProps & {
  uuid?: string,
  data?: IGroup,
  error: any | null,

  species: ISpecie[] | null,

  loading: boolean,
  contentLoading: boolean,
  history: any,

  loadGroup: (args: SearchRequest) => void,
  loadSpecies: (args: SearchRequest) => void,

  dispatchCreate: (args: GroupSaveRequest) => Promise<any>,
  dispatchUpdate: (args: GroupSaveRequest) => Promise<any>
}

export default class Group extends FormComponent<GroupProps> {

  static defaultProps = {
    contentLoading: true
  }

  state = {
    saving: false,
    loaded: false,
    speciesSelected: [] as string[],
    speciesSelection: {
      sourceSelectedKeys: [] as any,
      targetSelectedKeys: [] as any
    }
  }

  transferSpeciesColumns = {
    left: [
      {
        dataIndex: 'name',
        title: 'Specie',
        searchable: true,
        render: title => (
          <Typography.Text strong style={{fontSize: 12}}>{title}</Typography.Text>
        )
      }
    ],
    right: [
      {
        dataIndex: 'name',
        title: 'Specie',
        render: title => (
          <Typography.Text strong style={{fontSize: 12}}>{title}</Typography.Text>
        )
      }
    ]
  }

  componentDidMount() {
    const { loadSpecies } = this.props

    // Check permissions
    this.validateAccessPermissions()

    // Load species
    loadSpecies({
      options: {
        limit: 9999
      }
    })
  }

  componentDidUpdate(prevProps: GroupProps, prevState) {
    const wasLoading = prevProps.loading && !this.props.loading
    const loading = this.props.loading || this.props.contentLoading
    let newState = Object.assign({}, this.state)

    // Check permissions
    this.validateAccessPermissions()

    // We are loaded?
    if (!this.state.loaded) {
      // If our entity was loaded or if we are creating a new one, that's means that we are loaded :P
      if ((wasLoading || !this.props.uuid) && !loading) {
        if (this.props.uuid && this.props.data) {
          const { public_group_species } = this.props.data

          public_group_species?.forEach(p => {
            if (!newState.speciesSelected.includes(p.specie.uuid)) {
              newState.speciesSelected.push(p.specie.uuid)
            }
          })
        }

        newState.loaded = true
      } 

      // Load breed by uuid
      if (this.props.uuid && !newState.loaded && !loading) {
        this.props.loadGroup({
          search: {
            uuid: this.props.uuid,
            public_group_species: {
              with: {
                deleted: false,
                specie: {
                  with: true
                }
              }
            }
          }
        })
      }
    } else {
      // We are saving
      if (this.state.saving && wasLoading) {
        // Add the new role name to url
        if (!this.props.uuid && this.props.data) {
          this.props.history.replace(`/admin/group/${this.props.data.uuid}`)
        }

        if (!this.props.error) {
          message.success('Group saved successfully!')
        } else {
          message.error('Failed to save user. Error: ' + this.props.error)
        }

        newState.saving = false
      }
    }

    // Update if changed
    if (newState.loaded !== this.state.loaded) {
      this.setState(newState)
    }
  }

  handleSpecieSelection = (targetKeys: any, direction: any, moveKeys: any) => {
    this.setState({
      speciesSelected: targetKeys
    })
  }

  handleSelectionChange = (sourceSelectedKeys: string[], targetSelectedKeys: string[]) => {
    this.setState(state => ({
      ...state,
      speciesSelection: {
        sourceSelectedKeys,
        targetSelectedKeys
      }
    }))
  }

  handleSave = (values: any) => {
    const { uuid, dispatchCreate, dispatchUpdate } = this.props
    const { speciesSelected } = this.state

    let payload: GroupSaveRequest = {
      group: {
        ...values,
        species: speciesSelected.map(specieUuid => Object({uuid: specieUuid}))
      }
    }

    if (uuid) {
      payload = {
        group: {
          ...payload.group,
          uuid
        }
      }

      dispatchUpdate(payload)
      this.setState({
        saving: true
      })
    } else {
      dispatchCreate(payload)
      this.setState({
        saving: true
      })
    }
  }

  render() {
    const { uuid, data, species, loading } = this.props
    const { loaded, speciesSelected } = this.state

    if (!loaded) {
      return <Skeleton />
    }

    // Species options
    let groupSpecies = {} as any
    const specieOptions = species?.map(specie => {
      const selected = speciesSelected.includes(specie.uuid)
      const groupSpecie = data?.public_group_species?.find(x => x.specie.uuid = specie.uuid)

      if (selected && groupSpecie) {
        groupSpecies[specie.uuid] = groupSpecie
      }

      return {
        key: specie.uuid,
        name: specie.name,
        chosen: selected !== undefined
      }
    }) || []


    let values:any = {
      name: ''
    }

    if (uuid) {
      values = {
        ...data || {},
        species: groupSpecies
      }
    }

    return (
      <Form
        name="group"
        initialValues={values}
        layout="vertical"
        onFinish={this.handleSave}
      >
        <Row gutter={24}>
          <Col xs={24} md={6}>
            <Form.Item
              label="Name"
              name="name"
              rules={[
                {
                  required: true,
                  message: 'Missing a name for the group'
                }
              ]}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col xs={24} md={3}>
            <Form.Item
              label="Order"
              name="order"
              rules={[
                {
                  required: true,
                  message: 'Missing an order for the group'
                }
              ]}
            >
              <InputNumber min={0} />
            </Form.Item>
          </Col>

          <Col xs={24} md={3}>
            <Form.Item
              label="Highlight"
              name="highlight"
              rules={[
                {
                  required: true,
                  message: 'Choose the highlight enabled for the group'
                }
              ]}
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Typography.Title level={5}>Species</Typography.Title>

            <TableTransfer
              dataSource={specieOptions}
              targetKeys={speciesSelected}
              listStyle={null}
              leftColumns={this.transferSpeciesColumns.left}
              rightColumns={this.transferSpeciesColumns.right}
              onChange={this.handleSpecieSelection}
              onSelectChange={(sourceSelectedKeys: string[], targetSelectedKeys: string[]) => this.handleSelectionChange(sourceSelectedKeys, targetSelectedKeys)}
            />
          </Col>
        </Row>
        
        <Form.Item style={{marginTop: 32}}>
          <Button type="primary" htmlType="submit" size="large" loading={loading} ref={(refs) => this.actionRefs.save = refs} block>
            Save
          </Button>
        </Form.Item>
      </Form>
    )
  }
}